import {
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  stepConnectorClasses,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { StepIconProps } from '@mui/material/StepIcon';
import Iconify from 'src/components/iconify';

export interface FormStep {
  label: string;
  /** Iconify name shown instead of the dot on a narrow screen. */
  icon?: string;
}

interface FormStepperProps {
  steps: FormStep[];
  activeStep: number;
  /** Completed steps get a check and can be jumped back to. */
  completedSteps?: boolean[];
  /** Every step is reachable, not just the completed ones — affects the cursor. */
  allStepsReachable?: boolean;
  onStepClick?: (index: number) => void;
}

const DotConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    // Half the icon box. MUI insets the connector by 20px either side on the
    // assumption of a 24px icon, so the box has to be that size or the line
    // and the icon drift apart — visibly so once there are enough steps to
    // make each cell narrow.
    top: 12,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
  },
}));

/**
 * Always 24px, whatever it holds — a dot, a check or a per-step icon. The
 * contents are centred inside it rather than nudged into place, so nothing
 * needs to know how big the glyph is.
 */
const DotStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.action.disabledBackground,
  ...(ownerState.active && { color: theme.palette.primary.main }),
  ...(ownerState.completed && { color: theme.palette.primary.main }),
}));

const Dot = styled('span')<{ ownerState: { active?: boolean } }>(
  ({ theme, ownerState }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: ownerState.active
      ? theme.palette.primary.main
      : theme.palette.action.disabledBackground,
  }),
);

function DotStepIcon(
  props: StepIconProps & {
    isMobile: boolean;
    iconName?: string;
    label: string;
  },
) {
  const { active, completed, className, isMobile, iconName, label } = props;

  const icon = (
    <DotStepIconRoot ownerState={{ completed, active }} className={className}>
      {isMobile && iconName ? (
        <Iconify icon={iconName} width={24} />
      ) : completed ? (
        <Iconify color="#50CEFF" icon="ic:round-check" width={20} />
      ) : (
        <Dot ownerState={{ active }} />
      )}
    </DotStepIconRoot>
  );

  if (isMobile) {
    return (
      <Tooltip title={label} arrow placement="top">
        {icon}
      </Tooltip>
    );
  }

  return icon;
}

/**
 * The dotted step rail used by the booking modal, extracted so any multi-step
 * form in the dashboard reads the same: a small dot per step, a check once the
 * step is done, labels on desktop and per-step icons with a tooltip on mobile.
 */
export default function FormStepper({
  steps,
  activeStep,
  completedSteps,
  allStepsReachable = false,
  onStepClick,
}: FormStepperProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Stepper
      nonLinear
      alternativeLabel
      activeStep={activeStep}
      connector={<DotConnector />}
      sx={{
        marginTop: 1,
        width: '100%',
        mb: isSmallScreen ? 1 : 3,
        // Equal cells, whatever the labels say. A flex item will not shrink
        // below its content by default, so a long label like "Przypomnienie"
        // widened its own cell and stretched the connectors either side of it.
        '& .MuiStep-root': { flex: '1 1 0', minWidth: 0 },
      }}
    >
      {steps.map((step, index) => (
        <Step
          key={`step-${index}-${step.label}`}
          completed={completedSteps?.[index]}
        >
          <StepLabel
            slots={{
              stepIcon: (props) => (
                <DotStepIcon
                  {...props}
                  isMobile={isSmallScreen}
                  iconName={step.icon}
                  label={step.label}
                />
              ),
            }}
            onClick={() => onStepClick?.(index)}
            sx={{
              cursor:
                allStepsReachable || completedSteps?.[index]
                  ? 'pointer'
                  : 'default',
            }}
          >
            {/* Six labels do not fit across a phone, but a rail of bare icons
                does not say where you are. Only the step you are on is named,
                under its own icon — see the overflow rules on the Stepper for
                why it may spill past its cell. */}
            {!isSmallScreen || index === activeStep ? step.label : ''}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}
